import React, { useEffect, useState } from "react";
import { FaFacebook, FaInstagram, FaLinkedin, FaWhatsapp } from "react-icons/fa";

/* ─────────────────────────────────────────────
   TOKENS
───────────────────────────────────────────── */
const G   = "#6cbb23";
const ZH  = { fontFamily: "'Fraunces',Georgia,serif" };
const ZB  = { fontFamily: "'Plus Jakarta Sans',sans-serif" };

/* ─────────────────────────────────────────────
   STYLES
───────────────────────────────────────────── */
const Styles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,800;0,9..144,900;1,9..144,300;1,9..144,800;1,9..144,900&family=Plus+Jakarta+Sans:wght@300;400;500;600&display=swap');

    /* ── Marquee ── */
    @keyframes ft-marquee { from{transform:translateX(0)} to{transform:translateX(-50%)} }
    .ft-marquee-track { animation: ft-marquee 26s linear infinite; display:flex; width:max-content; }
    .ft-marquee-track:hover { animation-play-state:paused; }

    /* ── Shimmer on large heading ── */
    @keyframes ft-shimmer {
      0%   { background-position: -300% center; }
      100% { background-position:  300% center; }
    }
    .ft-shimmer {
      background: linear-gradient(90deg, #6cbb23 0%, #a8e063 30%, #6cbb23 55%, #4d8f10 100%);
      background-size: 200% auto;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      animation: ft-shimmer 4s linear infinite;
    }

    /* ── Links ── */
    .ft-link {
      ...ZB;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 13px; font-weight: 400; color: rgba(255,255,255,.38);
      text-decoration: none; display: block; padding: 4px 0;
      transition: color .18s, transform .18s;
    }
    .ft-link:hover { color: #6cbb23; transform: translateX(4px); }

    /* ── Social icons ── */
    .ft-social {
      width: 38px; height: 38px; border-radius: 10px;
      border: 1px solid rgba(255,255,255,.09);
      display: flex; align-items: center; justify-content: center;
      color: rgba(255,255,255,.4); text-decoration: none;
      transition: background .18s, border-color .18s, color .18s, transform .18s;
    }
    .ft-social:hover {
      background: #6cbb23; border-color: #6cbb23;
      color: #fff; transform: translateY(-3px);
    }

    /* ── WA button ── */
    .ft-wa-btn {
      display: inline-flex; align-items: center; justify-content: center; gap: 8px;
      background: #6cbb23; color: #fff; border: none; cursor: pointer;
      font-family: 'Plus Jakarta Sans',sans-serif; font-weight: 700; font-size: 14px;
      border-radius: 10px; padding: 13px 24px;
      text-decoration: none;
      transition: transform .18s, box-shadow .18s;
    }
    .ft-wa-btn:hover { transform: translateY(-2px); box-shadow: 0 12px 36px rgba(108,187,35,.42); }
    .ft-wa-btn:active { transform: scale(.97); }

    /* ── Bottom legal links ── */
    .ft-legal {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 11px; color: rgba(255,255,255,.22);
      text-decoration: none; transition: color .18s;
    }
    .ft-legal:hover { color: #6cbb23; }

    /* ── Col heading ── */
    .ft-col-h {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 10px; font-weight: 700;
      color: rgba(255,255,255,.22); letter-spacing: .12em;
      text-transform: uppercase; margin-bottom: 16px;
    }
  `}</style>
);

/* ─────────────────────────────────────────────
   HOOK — window width
───────────────────────────────────────────── */
function useW() {
  const [w, setW] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

/* ─────────────────────────────────────────────
   DATA
───────────────────────────────────────────── */
type FooterLink = { label: string; href?: string };

const cols: { heading: string; links: FooterLink[] }[] = [
  {
    heading: "Product",
    links: [
      { label: "Digital Menu"        },
      { label: "WhatsApp Ordering"   },
      { label: "Sales Analytics"     },
      { label: "GST Billing"         },
      { label: "Appointments"        },
      { label: "Customer Offers"     },
    ],
  },
  {
    heading: "Who It's For",
    links: [
      { label: "Restaurants & Cafés" },
      { label: "Hotels & Resorts"    },
      { label: "Salons & Spas"       },
      { label: "Home Chefs"          },
      { label: "Food Stalls"         },
      { label: "Any Order Business"  },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "Home",               href: "/"         },
      { label: "Login",              href: "/login"    },
      { label: "Book a Demo",        href: "#BookDemo" },
      { label: "Blogs",              href: "/blogs"    },
      { label: "Careers",            href: "/login"    },
      { label: "Terms & Conditions", href: "/terms"    },
    ],
  },
];

const socials = [
  { Icon: FaFacebook,  href: "https://www.facebook.com/zafflo",                                         label: "Facebook"  },
  { Icon: FaInstagram, href: "https://www.instagram.com/_zafflo/",                                      label: "Instagram" },
  { Icon: FaLinkedin,  href: "https://www.linkedin.com/company/zafflo/?viewAsMember=true",              label: "LinkedIn"  },
  { Icon: FaWhatsapp,  href: "https://wa.me/918097588099?text=Hi%20I%20want%20to%20know%20more%20about%20Zafflo", label: "WhatsApp"  },
];

const marqueItems = [
  { e: "🍽️", t: "Restaurants" },
  { e: "☕",  t: "Cafés"       },
  { e: "🏨",  t: "Hotels"      },
  { e: "💇",  t: "Salons"      },
  { e: "💆",  t: "Spas"        },
  { e: "👩‍🍳", t: "Home Chefs"  },
  { e: "🍕",  t: "Food Stalls" },
  { e: "🌿",  t: "Wellness"    },
];

/* ─────────────────────────────────────────────
   FOOTER
───────────────────────────────────────────── */
const Footer = () => {
  const w          = useW();
  const isMobile   = w < 640;
  const isTablet   = w >= 640 && w < 960;

  return (
    <footer style={{
      background: "#07100a",
      position: "relative", overflow: "hidden",
      fontFamily: "'Plus Jakarta Sans',sans-serif",
    }}>
      <Styles />

      {/* Subtle grid */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage: "linear-gradient(rgba(108,187,35,.028) 1px,transparent 1px),linear-gradient(90deg,rgba(108,187,35,.028) 1px,transparent 1px)",
        backgroundSize: "48px 48px",
      }}/>

      {/* Centre glow */}
      <div style={{
        position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)",
        width: 800, height: 400,
        background: "radial-gradient(ellipse,rgba(108,187,35,.06) 0%,transparent 65%)",
        pointerEvents: "none",
      }}/>

     

      {/* ── MARQUEE DIVIDER ── */}
      <div style={{
        borderBottom: "1px solid rgba(255,255,255,.06)",
        overflow: "hidden", position: "relative",
        background: "rgba(108,187,35,.025)",
      }}>
        {/* Fade masks */}
        {(["left","right"] as const).map(side => (
          <div key={side} style={{
            position: "absolute", top: 0, bottom: 0, [side]: 0, width: 80, zIndex: 2,
            background: `linear-gradient(to ${side === "left" ? "right" : "left"}, #07100a, transparent)`,
            pointerEvents: "none",
          }}/>
        ))}
        <div className="ft-marquee-track" style={{ padding: "14px 0" }}>
          {[...marqueItems, ...marqueItems, ...marqueItems].map(({ e, t }, i) => (
            <div key={i} style={{
              display: "inline-flex", alignItems: "center", gap: 7,
              padding: "0 28px",
              borderRight: "1px solid rgba(255,255,255,.06)",
              whiteSpace: "nowrap" as const,
            }}>
              <span style={{ fontSize: 14 }}>{e}</span>
              <span style={{ ...ZB, fontSize: 12, fontWeight: 500, color: "rgba(255,255,255,.3)" }}>{t}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── COLUMNS ── */}
      <div style={{
        maxWidth: 1100, margin: "0 auto", padding: "0 24px",
        position: "relative", zIndex: 1,
      }}>
        <div style={{
          padding: "52px 0 44px",
          borderBottom: "1px solid rgba(255,255,255,.06)",
          display: "grid",
          gridTemplateColumns: isMobile
            ? "1fr 1fr"
            : isTablet
            ? "1fr 1fr 1fr"
            : "1fr 1fr 1fr 1.4fr",
          gap: isMobile ? "36px 20px" : 40,
        }}>

          {/* 3 link columns */}
          {cols.map(col => (
            <div key={col.heading}>
              <div className="ft-col-h">{col.heading}</div>
              {col.links.map(({ label, href }) => (
                <a key={label} href={href ?? "#"} className="ft-link">{label}</a>
              ))}
            </div>
          ))}

          {/* 4th col — brand + social + stats */}
          <div style={{ gridColumn: isMobile ? "1 / -1" : "auto" }}>
            <div className="ft-col-h">Stay Connected</div>

            {/* Social icons */}
            <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
              {socials.map(({ Icon, href, label }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer"
                  className="ft-social" aria-label={label}>
                  <Icon style={{ fontSize: 14 }} />
                </a>
              ))}
            </div>

            {/* Contact */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 24 }}>
              <a href="tel:+918097588099" style={{ ...ZB, fontSize: 12, color: "rgba(255,255,255,.4)", textDecoration: "none", display: "flex", alignItems: "center", gap: 7, transition: "color .18s" }}
                onMouseOver={e => (e.currentTarget.style.color = G)}
                onMouseOut={e  => (e.currentTarget.style.color = "rgba(255,255,255,.4)")}
              >
                <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                  <path d="M2.5 2.5h3l1 2.5-1.5 1a6 6 0 003 3l1-1.5 2.5 1v3a1 1 0 01-1 1C5.5 13 1 8.5 1 3.5a1 1 0 011-1z" stroke={G} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                +91 80 975 88099
              </a>
              <a href="mailto:wecare@zafflo.com" style={{ ...ZB, fontSize: 12, color: "rgba(255,255,255,.4)", textDecoration: "none", display: "flex", alignItems: "center", gap: 7, transition: "color .18s" }}
                onMouseOver={e => (e.currentTarget.style.color = G)}
                onMouseOut={e  => (e.currentTarget.style.color = "rgba(255,255,255,.4)")}
              >
                <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                  <rect x="1" y="3" width="12" height="8" rx="1.5" stroke={G} strokeWidth="1.3"/>
                  <path d="M1 4l6 4 6-4" stroke={G} strokeWidth="1.3" strokeLinecap="round"/>
                </svg>
                wecare@zafflo.com
              </a>
            </div>

            {/* Three stats */}
            <div style={{
              display: "grid", gridTemplateColumns: "repeat(3,1fr)",
              gap: 10, paddingTop: 20,
              borderTop: "1px solid rgba(255,255,255,.07)",
            }}>
              {[
                { v: "50+",  l: "Businesses" },
                { v: "10K+", l: "Orders"     },
                { v: "5.0★", l: "Rating"     },
              ].map(({ v, l }) => (
                <div key={l} style={{ textAlign: "center" as const }}>
                  <div style={{ ...ZH, fontSize: 18, fontWeight: 900, fontStyle: "italic", color: G, lineHeight: 1 }}>{v}</div>
                  <div style={{ ...ZB, fontSize: 10, color: "rgba(255,255,255,.22)", marginTop: 4 }}>{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── BOTTOM BAR ── */}
        <div style={{
          padding: "18px 0 28px",
          display: "flex", flexWrap: "wrap" as const,
          alignItems: "center", justifyContent: "space-between", gap: 12,
        }}>
          {/* Left — copyright */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            
            <span style={{ ...ZB, fontSize: 11, color: "rgba(255,255,255,.22)" }}>
              © {new Date().getFullYear()} Zafflo · Made with ♥ in India
            </span>
          </div>

          {/* Right — legal links */}
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap" as const, alignItems: "center" }}>
            {[
              { t: "Privacy Policy",       h: "/terms"      },
              { t: "Terms & Conditions",   h: "/terms" },
              { t: "sitemap",              h: "#"      },
            ].map(({ t, h }) => (
              <a key={t} href={h} className="ft-legal">{t}</a>
            ))}
            <span style={{ ...ZB, fontSize: 11, color: "rgba(255,255,255,.14)" }}>🇮🇳 Proudly Indian</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;